import React, { useState } from 'react';
import { Card, Row, Col, Button, Modal, Form, Input, InputNumber, Select, Upload, Popconfirm, Spin, Empty, Typography, Divider, Image } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, MinusCircleOutlined, EyeOutlined } from '@ant-design/icons';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { firestore, storage } from 'config/firebase';
import { useProductsContext } from 'contexts/ProductsContext';
import slugify from 'slugify';
import { Link } from 'react-router-dom';

const { Option } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;

const ProductList = () => {
    const { products, fetchProducts, isProductsLoading } = useProductsContext();
    const [isUpdatingProduct, setIsUpdatingProduct] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [localImages, setLocalImages] = useState([]); // to store local image files

    const [form] = Form.useForm();

    const handleEdit = (product) => {
        setEditingProduct(product);
        form.setFieldsValue({
            ...product,
            images: product.images.map((url, index) => ({
                uid: `-${index}`,
                name: `image-${index}`,
                status: 'done',
                url,
            })),
        });
        setEditModalVisible(true);
    };

    const handleImageChange = ({ fileList }) => {
        const newImages = fileList.filter(file => !file.url);
        const retainedImages = fileList.filter(file => file.url);

        // Update state
        setLocalImages(newImages);
        form.setFieldsValue({ images: [...retainedImages, ...newImages] });
    };

    const handleUpdate = async (values) => {
        setIsUpdatingProduct(true);
        try {
            const productRef = doc(firestore, 'products', editingProduct.id);

            // Handle image updates
            const currentImages = editingProduct.images;
            const updatedImages = values.images.map(img => img.url || img.response);
            const imagesToDelete = currentImages.filter(img => !updatedImages.includes(img));

            // Delete removed images from storage
            await Promise.all(imagesToDelete.map(async (imgUrl) => {
                const imgRef = ref(storage, imgUrl);
                await deleteObject(imgRef);
            }));


            const newImageUrls = await uploadImages(localImages);

            const finalImages = [
                ...updatedImages.filter(img => typeof img === 'string'),
                ...newImageUrls
            ];

            const updatedProduct = {
                ...values,
                images: finalImages,
                slug: slugify(values.name, { lower: true }),
                updatedAt: new Date(),
            };

            await updateDoc(productRef, updatedProduct);
            window.toastify('Product updated successfully', "success");
            setEditModalVisible(false);
            fetchProducts();
        } catch (error) {
            console.error('Error updating product:', error);
            window.toastify('Failed to update product', 'error');
        } finally {
            setIsUpdatingProduct(false);
        }
    };

    const handleDelete = async (productId) => {
        try {
            const productToDelete = products.find(p => p.id === productId);

            // Delete images from storage
            await Promise.all(productToDelete.images.map(async (imgUrl) => {
                const imgRef = ref(storage, imgUrl);
                await deleteObject(imgRef);
            }));

            await deleteDoc(doc(firestore, 'products', productId));
            window.toastify('Product deleted successfully', "success");
            fetchProducts();
        } catch (error) {
            console.error('Error deleting product:', error);
            window.toastify('Failed to delete product', 'error');
        }
    };

    const uploadImages = async (files) => {
        try {
            return await Promise.all(
                files.map(async (file) => {
                    const storageRef = ref(storage, `products/${window.getRandomId()}_${file.name}`);
                    const uploadTask = uploadBytesResumable(storageRef, file.originFileObj);
                    await uploadTask;
                    return await getDownloadURL(storageRef);
                })
            );
        } catch (error) {
            console.error('Error uploading images:', error);
            window.toastify('Failed to upload images. Please try again.', "error");
            throw error;
        }
    };

    if (isProductsLoading) {
        return <Spin size="large" />;
    }

    if (products.length === 0) {
        return (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span>No products found. Start by adding some products!</span>}>
                <Button type="primary" onClick={() => { }}>
                    Add Product
                </Button>
            </Empty>
        );
    }

    return (
        <div style={{ padding: '24px' }}>
            <Title level={2}>Product List</Title>
            <Row gutter={[16, 16]}>
                {products.map(product => (
                    <Col xs={24} sm={12} md={8} lg={6} key={product.id}>
                        <div className="product-image-container mb-0 pb-0">
                            <Image src={product.images[0]} alt={product.name} preview={false} width={"100%"} height={350} />
                            <div className="product-overlay">
                                <div className="overlay-icons">
                                    <Button icon={<EditOutlined />} shape="circle" onClick={() => handleEdit(product)} />
                                    <Popconfirm title="Are you sure you want to delete this product?" onConfirm={() => handleDelete(product.id)} okText="Yes" cancelText="No">
                                        <Button icon={<DeleteOutlined />} shape="circle" />
                                    </Popconfirm>,
                                    <Link to={`/product?category=${encodeURIComponent(product.slug)}`} className=' text-decoration-none'>
                                        <Button icon={<EyeOutlined />} shape="circle" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                        <Card className="bg-transparent border-0 product-card m-0 p-0">
                            <Link to={`/product?category=${encodeURIComponent(product.slug)}`} className=' text-decoration-none'>
                                <Text strong className=' fw-bold fs-5'>{product.name}</Text>
                            </Link>
                            <br />
                            <Text strong>{product.description}</Text>
                            <div className="pricing mt-2">
                                <Text delete className="fs-6">${product.price.toFixed(2)}</Text>
                                <Text strong className="text-primary ml-2 ms-3 fs-5">${product.originalPrice.toFixed(2)}</Text>
                            </div>
                        </Card>
                    </Col>
                ))}
            </Row>

            <Modal title="Edit Product" open={editModalVisible} onCancel={() => setEditModalVisible(false)} footer={null} width={800}>
                <Form form={form} onFinish={handleUpdate} layout="vertical">
                    <Row gutter={24}>
                        <Col span={12}>
                            <Form.Item name="name" label="Product Name" rules={[{ required: true, message: 'Please enter the product name' }]}>
                                <Input placeholder="e.g., Comfortable Cotton T-Shirt" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="brand" label="Brand" rules={[{ required: true, message: 'Please enter the brand name' }]}>
                                <Input placeholder="e.g., FashionBrand" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        <Col span={12}>
                            <Form.Item name="price" label="Price" rules={[{ required: true, type: 'number', min: 0, message: 'Please enter a valid price' }]}>
                                <InputNumber formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={value => value.replace(/\$\s?|(,*)/g, '')} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="originalPrice" label="Original Price" rules={[{ required: true, type: 'number', min: 0, message: 'Please enter a valid original price' }]}>
                                <InputNumber formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={value => value.replace(/\$\s?|(,*)/g, '')} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="shortDescription" label="Short Description" rules={[{ required: true, message: 'Please enter a short description' }]}>
                        <TextArea rows={2} placeholder="Brief product description" />
                    </Form.Item>

                    <Form.Item name="description" label="Full Description" rules={[{ required: true, message: 'Please enter a full description' }]}>
                        <TextArea rows={4} placeholder="Detailed product description" />
                    </Form.Item>

                    <Row gutter={24}>
                        <Col span={12}>
                            <Form.Item name="colors" label="Colors" rules={[{ required: true, message: 'Please select at least one color' }]}>
                                <Select mode="multiple" placeholder="Select colors" style={{ width: '100%' }}>
                                    <Option value="pink">Pink</Option>
                                    <Option value="blue">Blue</Option>
                                    <Option value="purple">Purple</Option>
                                    <Option value="yellow">Yellow</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="sizes" label="Sizes" rules={[{ required: true, message: 'Please select at least one size' }]}>
                                <Select mode="multiple" placeholder="Select sizes" style={{ width: '100%' }}>
                                    <Option value="S">S</Option>
                                    <Option value="M">M</Option>
                                    <Option value="L">L</Option>
                                    <Option value="XL">XL</Option>
                                    <Option value="XXL">XXL</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider orientation="left">Product Features</Divider>

                    <Form.List name="features">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map((field, index) => (
                                    <Form.Item required={false} key={field.key}>
                                        <Form.Item {...field} validateTrigger={['onChange', 'onBlur']} rules={[{ required: true, whitespace: true, message: "Please input feature or delete this field.", },]} noStyle>
                                            <Input placeholder="Enter a product feature" style={{ width: 'calc(100% - 32px)' }} />
                                        </Form.Item>
                                        {fields.length > 1 && (
                                            <MinusCircleOutlined className="dynamic-delete-button" onClick={() => remove(field.name)} style={{ marginLeft: '8px' }} />
                                        )}
                                    </Form.Item>
                                ))}
                                <Form.Item>
                                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                        Add Feature
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>
                    <Form.Item name="images" label="Product Images" valuePropName="fileList" getValueFromEvent={e => e.fileList}>
                        <Upload listType="picture-card" fileList={form.getFieldValue('images')} onChange={handleImageChange} beforeUpload={() => false} multiple>
                            {form.getFieldValue('images') && form.getFieldValue('images').length < 4 ? <PlusOutlined /> : null}
                        </Upload>
                        <Text type="secondary">Maximum 4 images.</Text>
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={isUpdatingProduct} disabled={isUpdatingProduct} block>
                            Update Product
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ProductList;